import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignUpWithOAuth } from './useSignUpWithOAuth';
import {
  openOAuthPopup,
  redirectToOAuth,
  OAuthError,
  OAuthFailure,
  OAuthIdentity,
  OAuthProvider,
} from '../oauth';
import { usePendingOAuthIdentity } from './usePendingOAuthIdentity';
import { usePopupCapability } from '@/shared/hooks/usePopupCapability';
import { trackEvent, tagClaritySession } from '@/shared/analytics';
import { trackFormServerErrors } from '@/shared/analytics/formServerErrors';
import type { OAuthProfileFormValues } from '../OAuthProfileForm';

const OAUTH_ERROR_MESSAGE_IDS: Record<OAuthFailure, string> = {
  popup_blocked: 'auth.oauth.error.popupBlocked',
  popup_closed: 'auth.oauth.error.popupClosed',
  timeout: 'auth.oauth.error.timeout',
  stale_session: 'auth.oauth.error.staleSession',
  unknown: 'auth.oauth.error.unknown',
};

function failureOf(error: unknown): OAuthFailure {
  return error instanceof OAuthError ? error.failure : 'unknown';
}

export function useOAuthFlow() {
  const queryClient = useQueryClient();
  const popupsAreReliable = usePopupCapability();
  const {
    mutateAsync: completeSignUp,
    reset: resetSignUp,
    isPending: isCompleting,
    error: completeError,
  } = useSignUpWithOAuth();

  const [popupIdentity, setPopupIdentity] = useState<OAuthIdentity | null>(null);
  const [popupErrorMessageId, setPopupErrorMessageId] = useState<string | null>(null);

  const pending = usePendingOAuthIdentity();
  const identity = popupIdentity ?? pending.identity;

  const start = async (provider: OAuthProvider) => {
    setPopupErrorMessageId(null);

    try {
      if (!popupsAreReliable) {
        tagClaritySession('funnel_stage', 'oauth_redirect');
        trackEvent('oauth_redirect_start', { provider });
        redirectToOAuth(provider);
        return;
      }

      tagClaritySession('funnel_stage', 'oauth_popup');
      trackEvent('oauth_popup_open', { provider });

      const result = await openOAuthPopup(provider);
      if (result.status === 'authenticated') {
        trackEvent('oauth_sign_in', { provider });
        await queryClient.invalidateQueries({ queryKey: ['me'] });
        return;
      }
      setPopupIdentity(result.identity);
      trackEvent('oauth_profile_shown', { provider });
    } catch (error) {
      const failure = failureOf(error);
      trackEvent('oauth_popup_error', { provider, error_reason: failure });
      setPopupErrorMessageId(OAUTH_ERROR_MESSAGE_IDS[failure]);
    }
  };

  const complete = async (values: OAuthProfileFormValues) => {
    if (!identity) return;

    try {
      await completeSignUp({
        oauth_token: identity.oauth_token,
        name: values.name,
        country: values.country,
        phone: values.phone,
        terms_accepted: values.termsAccepted,
      });
      pending.forget();
      trackEvent('sign_up', { method: 'oauth' });
      tagClaritySession('funnel_stage', 'sign_up_complete');
      tagClaritySession('sign_up_method', 'oauth');
    } catch (error) {
      trackFormServerErrors(error, 'oauth_profile');
    }
  };

  const clear = () => {
    if (!identity) return;

    setPopupIdentity(null);
    pending.discard();
    resetSignUp();
  };

  const dismissPopupError = () => setPopupErrorMessageId(null);

  return {
    identity,
    popupErrorMessageId,
    completeError,
    isCompleting,
    start,
    complete,
    clear,
    dismissPopupError,
  };
}
